"use strict";

/* ─────────────────────────────────────────────
   STATE
───────────────────────────────────────────── */
let blendsType            = null;
let roastingProduct       = null;
let groundType            = null;
let groundImage           = null;
let quantity              = null;
let deliverySchedule      = null;
let deliveryScheduleImage = null;
let productInfo           = null;
let productInfoPromise    = null;

const subscriptionState = {
  variantId:     null,
  sellingPlanId: null,
  priceCents:    null,
  image:         null
};

/* ─────────────────────────────────────────────
   ABORT CONTROLLERS
   One per step. Abort = remove all listeners for
   that step so fresh ones can be added cleanly.
───────────────────────────────────────────── */
const controllers = {
  blend:    null,
  roast:    null,
  ground:   null,
  rotQty:   null,
  roastQty: null,
  schedule: null,
};

function resetController(key) {
  if (controllers[key]) controllers[key].abort();
  controllers[key] = new AbortController();
  return controllers[key].signal;
}

/* ─────────────────────────────────────────────
   ELEMENTS
───────────────────────────────────────────── */
const rotatingBlendSection    = document.querySelector(".choose-rotating-blends");
const roastingSection         = document.querySelector(".choose-roasting");
const groundSection           = document.querySelector(".choose-ground");
const rotatingquantitySection = document.querySelector(".choose-rotating-quantity");
const roastingquantitySection = document.querySelector(".choose-roasting-quantity");
const scheduleSection         = document.querySelector(".choose-schedule");
const summarySection          = document.querySelector(".order-summary");

const commonBtnClass     = ".homepage-section-btn";
const commonHeadingClass = ".widget-block--grid-heading";
const productDropdown    = document.getElementById("product-select");

const rotatingQuantity = ["(1) 12oz bag", "(1) 2lb bag", "(1) 5lb bag"];

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
function scrollToSection(section) {
  if (!section) return;
  const header = document.querySelector(".header-section");
  const offset = header ? header.offsetHeight : 0;
  window.scrollTo({ top: section.offsetTop - offset, behavior: "smooth" });
}

async function fetchProduct(handle) {
  try {
    const res = await fetch(`/products/${handle}.js`);

    // If Shopify returns a 404 or error page, res.ok will be false
    // and the body will be HTML, not JSON — catch both cases
    if (!res.ok) {
      console.error(`Product not found: "${handle}" (HTTP ${res.status})`);
      return null;
    }

    const data = await res.json();

    // Extra sanity check — make sure it looks like a product
    if (!data || !data.variants || !Array.isArray(data.variants)) {
      console.error(`Product "${handle}" returned unexpected data shape:`, data);
      return null;
    }

    return data;
  } catch (err) {
    console.error(`fetchProduct error for "${handle}":`, err);
    return null;
  }
}

function resetButtons(scope) {
  if (!scope) return;
  scope.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.classList.remove("selected");
    btn.innerText = "Select";
  });
}

function hideAndReset(section) {
  if (!section) return;
  section.classList.add("hide");
  resetButtons(section);
}

/**
 * Clear all steps at and after fromStep, and abort their listeners.
 * Steps: 0=ground, 1=quantity, 2=schedule, 3=summary
 */
function clearDownstream(fromStep) {
  if (fromStep <= 0) {
    hideAndReset(groundSection);
    resetController("ground");
    groundType  = null;
    groundImage = null;
  }
  if (fromStep <= 1) {
    hideAndReset(rotatingquantitySection);
    hideAndReset(roastingquantitySection);
    resetController("rotQty");
    resetController("roastQty");
    quantity = null;
  }
  if (fromStep <= 2) {
    hideAndReset(scheduleSection);
    resetController("schedule");
    deliverySchedule      = null;
    deliveryScheduleImage = null;
  }
  // Always clear summary state when anything upstream changes
  hideAndReset(summarySection);
  subscriptionState.variantId     = null;
  subscriptionState.sellingPlanId = null;
  subscriptionState.priceCents    = null;
  subscriptionState.image         = null;
}

/* ─────────────────────────────────────────────
   VARIANT MATCHING
───────────────────────────────────────────── */
function findMatchingVariant(product, qty, grind, blend) {
  // Normalize to lowercase for case-insensitive comparison
  const qtyL   = qty ? qty.toLowerCase().trim() : null;
  const grindL = grind ? grind.toLowerCase().trim() : null;
  const blendL = blend ? blend.toLowerCase().trim() : null;
  if (!product || !qtyL || !grindL) return null;

  // Full 3-option match (case-insensitive)
  let match = product.variants.find(v =>
    v.option1?.toLowerCase().trim() === qtyL &&
    v.option2?.toLowerCase().trim() === grindL &&
    v.option3 && blendL && v.option3.toLowerCase().includes(blendL)
  );

  // 2-option fallback (roasting products have no option3)
  if (!match) {
    match = product.variants.find(v =>
      v.option1?.toLowerCase().trim() === qtyL &&
      v.option2?.toLowerCase().trim() === grindL
    );
  }

  return match || null;
}

/* ─────────────────────────────────────────────
   SELLING PLAN MATCHING
───────────────────────────────────────────── */
function findSellingPlan(product, schedule) {
  if (!product?.selling_plan_groups || !schedule) return null;

  const sel = schedule.toLowerCase().trim();
  let selectedWeeks = null;

  if (sel.includes("every week") || sel === "weekly" || sel.includes("1 week")) {
    selectedWeeks = 1;
  } else if (sel.includes("every two weeks") || sel.includes("2 weeks") || sel === "biweekly") {
    selectedWeeks = 2;
  } else if (sel.includes("every three weeks") || sel.includes("3 weeks")) {
    selectedWeeks = 3;
  } else if (sel.includes("every four weeks") || sel.includes("4 weeks") ||
             sel.includes("monthly") || sel.includes("every month")) {
    selectedWeeks = 4;
  }

  for (const group of product.selling_plan_groups) {
    for (const plan of group.selling_plans) {
      const m = plan.name.toLowerCase().match(/every (\d+) weeks?/);
      const planWeeks = m ? parseInt(m[1], 10) : null;
      if (planWeeks && selectedWeeks && planWeeks === selectedWeeks) {
        console.log(`Selling plan matched: "${plan.name}" (ID: ${plan.id})`);
        return plan.id;
      }
    }
  }

  console.warn("No selling plan matched for:", schedule);
  return null;
}

/* ─────────────────────────────────────────────
   UPDATE STATE + RENDER
───────────────────────────────────────────── */
async function updateAndRender() {
  if (!productInfo && productInfoPromise) {
    productInfo = await productInfoPromise;
  }
  if (!productInfo) return;

  const variant = findMatchingVariant(productInfo, quantity, groundType, blendsType);
  if (!variant) {
    console.warn("No variant matched:", { quantity, groundType, blendsType });
    return;
  }

  subscriptionState.variantId  = variant.id;
  subscriptionState.priceCents = variant.price;
  subscriptionState.image      =
    variant.featured_image?.src ||
    productInfo.featured_image?.src ||
    productInfo.images?.[0];

  subscriptionState.sellingPlanId = deliverySchedule
    ? findSellingPlan(productInfo, deliverySchedule)
    : null;

  renderSummary();
}

function getShippingCost(qty) {
  if (!qty) return 0;
  return (qty.includes("12oz") || qty.includes("12 oz")) ? 500 : 0;
}

function renderSummary() {
  if (!subscriptionState.variantId || subscriptionState.priceCents == null) return;

  let priceCents = subscriptionState.priceCents;

  // 5% discount on specific roast path
  if (roastingProduct && (!rotatingBlendSection || rotatingBlendSection.classList.contains("hide"))) {
    priceCents = Math.round(priceCents * 0.95);
  }

  const shippingCents = getShippingCost(quantity);
  const totalCents    = priceCents + shippingCents;

  const imageEl = document.querySelector(".subscription-order--summary-product--image");
  if (imageEl && subscriptionState.image) imageEl.setAttribute("src", subscriptionState.image);

  document.querySelectorAll(".subscription-order--summary-product--price")
    .forEach(el => (el.innerText = `$${(priceCents / 100).toFixed(2)}`));

  document.querySelectorAll(".total_subscription")
    .forEach(el => (el.innerText = `$${(totalCents / 100).toFixed(2)}`));

  const is12oz = quantity && (quantity === "12 oz" || quantity.includes("12oz"));
  document.querySelectorAll(".subscription-order--summary-item .subscription-order--summary-info")
    .forEach(item => {
      const shippingEl   = item.querySelector(".shippingcharge span");
      const noShippingEl = item.querySelector(".noShippingCharge span");
      if (is12oz) {
        if (shippingEl)   { shippingEl.parentElement.style.display = "flex"; shippingEl.innerText = "$5.00"; }
        if (noShippingEl)   noShippingEl.parentElement.style.display = "none";
      } else {
        if (shippingEl)     shippingEl.parentElement.style.display = "none";
        if (noShippingEl) { noShippingEl.parentElement.style.display = "flex"; noShippingEl.innerText = "Free"; }
      }
    });
}

/* ─────────────────────────────────────────────
   HIDE ALL ON INIT
───────────────────────────────────────────── */
function hideSubscriptionWidgets() {
  document.querySelectorAll(".subscription-widget-block").forEach(el => el.classList.add("hide"));
}
hideSubscriptionWidgets();

const subscriptionRoot = document.querySelector(".subscription-text-with-image-overlay");
if (subscriptionRoot) {
  new MutationObserver(hideSubscriptionWidgets)
    .observe(subscriptionRoot, { childList: true, subtree: true });
}

/* ─────────────────────────────────────────────
   STEP 1A — ROTATING BLENDS
───────────────────────────────────────────── */
function attachRotatingBlend() {
  const signal = resetController("blend");

  // Seed quantity labels for rotating path
  document.querySelectorAll(`.choose-rotating-quantity ${commonHeadingClass}`)
    .forEach((el, i) => (el.innerText = rotatingQuantity[i] || ""));

  rotatingBlendSection.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.addEventListener("click", async e => {
      e.preventDefault();

      resetButtons(rotatingBlendSection);
      btn.classList.add("selected");
      btn.innerText = "Selected";

      blendsType = btn.closest(".widget-block--grid-item")
        .querySelector(commonHeadingClass).innerText;

      // Changing blend resets everything downstream
      clearDownstream(0);

      productInfoPromise = fetchProduct("doma-subscription");
      productInfo = await productInfoPromise;

      document.querySelector(".subscription-order--summary-product--title").innerText = blendsType;

      groundSection.classList.remove("hide");
      attachGround("rotating");
      scrollToSection(groundSection);

    }, { signal });
  });
}

/* ─────────────────────────────────────────────
   STEP 1B — SPECIFIC ROAST
───────────────────────────────────────────── */
function attachRoasting() {
  const signal = resetController("roast");

  roastingSection.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.addEventListener("click", async e => {
      e.preventDefault();

      resetButtons(roastingSection);
      btn.classList.add("selected");
      btn.innerText = "Selected";

      blendsType      = btn.closest(".widget-block--grid-item").querySelector(commonHeadingClass).innerText;
      roastingProduct = btn.dataset.productHandle;

      // Changing roast resets everything downstream
      clearDownstream(0);

      productInfoPromise = fetchProduct(roastingProduct);
      productInfo = await productInfoPromise;

      if (!productInfo) {
        console.error(`Failed to load product: "${roastingProduct}"`);
        alert("Sorry, we couldn't load that product. Please try another selection or refresh the page.");
        btn.classList.remove("selected");
        btn.innerText = "Select";
        return;
      }

      // Populate roasting quantity labels from product Size option
      const sizeOption = productInfo?.options?.find(o => o.name === "Size");
      if (sizeOption) {
        document.querySelectorAll(`.choose-roasting-quantity ${commonHeadingClass}`)
          .forEach((el, i) => (el.innerText = sizeOption.values[i] || ""));
      }

      document.querySelector(".subscription-order--summary-product--title").innerText = blendsType;

      groundSection.classList.remove("hide");
      attachGround("roasting");
      scrollToSection(groundSection);

    }, { signal });
  });
}

/* ─────────────────────────────────────────────
   STEP 2 — GRIND
───────────────────────────────────────────── */
function attachGround(type) {
  const signal = resetController("ground");

  groundSection.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.addEventListener("click", e => {
      e.preventDefault();

      resetButtons(groundSection);
      btn.classList.add("selected");
      btn.innerText = "Selected";

      groundType  = btn.closest(".widget-block--grid-item").querySelector(commonHeadingClass).innerText;
      groundImage = btn.dataset.imageSrc;

      // Changing grind resets quantity and everything after
      clearDownstream(1);

      document.querySelector(".subscription-order--summary-option-image")
        .setAttribute("src", groundImage || "");
      document.querySelector(".subscription-order--summary-option--title").innerText = groundType;

      if (type === "rotating") {
        roastingquantitySection.classList.add("hide");
        rotatingquantitySection.classList.remove("hide");
        attachRotatingQuantity();
        scrollToSection(rotatingquantitySection);
      } else {
        rotatingquantitySection.classList.add("hide");
        roastingquantitySection.classList.remove("hide");
        attachRoastingQuantity();
        scrollToSection(roastingquantitySection);
      }

    }, { signal });
  });
}

/* ─────────────────────────────────────────────
   STEP 3 — QUANTITY (rotating)
───────────────────────────────────────────── */
function attachRotatingQuantity() {
  const signal = resetController("rotQty");

  rotatingquantitySection.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.addEventListener("click", e => {
      e.preventDefault();

      resetButtons(rotatingquantitySection);
      btn.classList.add("selected");
      btn.innerText = "Selected";

      quantity = btn.closest(".widget-block--grid-item").querySelector(commonHeadingClass).innerText;

      // Changing quantity resets schedule and summary
      clearDownstream(2);

      document.querySelector(".subscription-order--summary-product--size").innerText = quantity;

      scheduleSection.classList.remove("hide");
      attachSchedule();
      scrollToSection(scheduleSection);

    }, { signal });
  });
}

/* ─────────────────────────────────────────────
   STEP 3 — QUANTITY (roasting)
───────────────────────────────────────────── */
function attachRoastingQuantity() {
  const signal = resetController("roastQty");

  roastingquantitySection.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.addEventListener("click", e => {
      e.preventDefault();

      resetButtons(roastingquantitySection);
      btn.classList.add("selected");
      btn.innerText = "Selected";

      quantity = btn.closest(".widget-block--grid-item").querySelector(commonHeadingClass).innerText;

      // Changing quantity resets schedule and summary
      clearDownstream(2);

      document.querySelector(".subscription-order--summary-product--size").innerText = quantity;

      scheduleSection.classList.remove("hide");
      attachSchedule();
      scrollToSection(scheduleSection);

    }, { signal });
  });
}

/* ─────────────────────────────────────────────
   STEP 4 — SCHEDULE
───────────────────────────────────────────── */
function attachSchedule() {
  const signal = resetController("schedule");

  scheduleSection.querySelectorAll(commonBtnClass).forEach(btn => {
    btn.addEventListener("click", async e => {
      e.preventDefault();

      resetButtons(scheduleSection);
      btn.classList.add("selected");
      btn.innerText = "Selected";

      deliverySchedule      = btn.closest(".widget-block--grid-item").querySelector(commonHeadingClass).innerText;
      deliveryScheduleImage = btn.dataset.imageSrc;

      document.querySelector(".subscription-order--summary-schedule-image")
        .setAttribute("src", deliveryScheduleImage || "");
      document.querySelector(".subscription-order--summary-schedule--title").innerText = deliverySchedule;

      await updateAndRender();

      summarySection.classList.remove("hide");
      scrollToSection(summarySection);

    }, { signal });
  });
}

/* ─────────────────────────────────────────────
   TOP-LEVEL PATH SWITCH
───────────────────────────────────────────── */
function switchToRotating() {
  roastingSection.classList.add("hide");
  rotatingBlendSection.classList.remove("hide");

  // Full reset when switching paths
  roastingProduct    = null;
  productInfo        = null;
  productInfoPromise = null;
  blendsType         = null;

  clearDownstream(0);
  resetButtons(rotatingBlendSection);

  attachRotatingBlend();
  scrollToSection(rotatingBlendSection);
}

function switchToRoasting() {
  rotatingBlendSection.classList.add("hide");
  roastingSection.classList.remove("hide");

  productInfo        = null;
  productInfoPromise = null;
  blendsType         = null;

  clearDownstream(0);
  resetButtons(roastingSection);

  attachRoasting();
  scrollToSection(roastingSection);
}

productDropdown?.addEventListener("change", e => {
  e.preventDefault();
  if (e.target.value === "Rotating Selection") switchToRotating();
  if (e.target.value === "Specific Roasts")    switchToRoasting();
});

document.querySelectorAll(".swap--section").forEach(button => {
  button.addEventListener("click", function (e) {
    e.preventDefault();
    this.innerText.trim() === "Rotating Selection"
      ? switchToRotating()
      : switchToRoasting();
  });
});

/* ─────────────────────────────────────────────
   SUBSCRIBE BUTTON
───────────────────────────────────────────── */
document.querySelector("#subscription--add-to-cart")
  .addEventListener("click", function () {
    if (!subscriptionState.variantId) {
      alert("Please complete all steps before subscribing.");
      return;
    }
    if (!subscriptionState.sellingPlanId) {
      alert("Please select a delivery schedule before subscribing.");
      return;
    }

    buildCart(subscriptionState.variantId, subscriptionState.sellingPlanId);

    if (window.isCustomer !== "true") {
      const d = new Date();
      d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * 30);
      document.cookie = `isLoggedIn=false;path=/cart;expires=${d.toGMTString()}`;
    }
  });

function buildCart(variantId, sellingPlanId) {
  const button = document.querySelector("#subscription--add-to-cart");
  button.innerText = "Subscribing...";

  console.log("Building cart:", { variantId, sellingPlanId });

  fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1, selling_plan: sellingPlanId }]
    }),
  })
    .then(response => {
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      return response.json();
    })
    .then(json => {
      console.log("Cart add success:", json);
      button.innerText = "Subscribe";
      window.location.href = window.isCustomer === "true"
        ? "https://www.domacoffee.com"
        : "/account/login";
    })
    .catch(err => {
      console.error("Cart error:", err);
      button.innerText = "Subscribe";
      alert("Something went wrong. Please try again.");
    });
}